/**
 * HRSN answer humanizer — converts the raw JSON answers stored in
 * `screening_answers.answers` to readable rows for admin/uploader screening
 * detail views.
 *
 * Created 2026-05-09 (user feedback: raw JSON looked "rústico").
 *
 * Each domain has its own answer schema (see api/src/screenings/hrsn-spec.ts).
 * Exposes humanizeAnswer(domain, answers, lang) which returns an array of
 * { label, value, positive? } rows the UI renders as a labelled table.
 *
 * Bilingual (en + es). New keys added to i18n.ts.
 */

export type HrsnDomain =
  | 'living_situation'
  | 'housing_conditions'
  | 'utilities'
  | 'food'
  | 'transportation'
  | 'career'
  | 'school'
  | 'safety'
  | 'physical_ability';

export interface HumanizedRow {
  /** Human-readable field label (already translated). */
  label: string;
  /** Human-readable value (already translated). */
  value: string;
  /** When true, this row represents a positive HRSN finding (concern flag). */
  positive?: boolean;
}

type Lang = 'en' | 'es';

// --- Localized maps ---------------------------------------------------------

const t = {
  // Section labels (so we don't depend on the i18n bundle inside this module).
  domain: {
    living_situation: { en: 'Living situation', es: 'Situación de vivienda' },
    housing_conditions: { en: 'Housing conditions', es: 'Condiciones de la vivienda' },
    utilities: { en: 'Utilities', es: 'Servicios públicos' },
    food: { en: 'Food security', es: 'Seguridad alimentaria' },
    transportation: { en: 'Transportation', es: 'Transporte' },
    career: { en: 'Employment', es: 'Empleo' },
    school: { en: 'Education', es: 'Educación' },
    safety: { en: 'Safety', es: 'Seguridad' },
    physical_ability: { en: 'Physical ability', es: 'Capacidad física' },
  },
  living: {
    steady: { en: 'Stable housing', es: 'Vivienda estable' },
    worried: { en: 'Worried about losing housing', es: 'Preocupado/a por perder la vivienda' },
    unstable: { en: 'No stable housing', es: 'Sin vivienda estable' },
    declined: { en: 'Declined to answer', es: 'Prefirió no responder' },
  },
  housingCondition: {
    pests: { en: 'Pests (rats, bugs)', es: 'Plagas (ratas, insectos)' },
    mold: { en: 'Mold', es: 'Moho' },
    lead: { en: 'Lead paint or pipes', es: 'Plomo en pintura o tuberías' },
    no_heat: { en: 'No heat', es: 'Sin calefacción' },
    oven_broken: { en: 'Oven/stove broken', es: 'Horno/estufa no funciona' },
    smoke_detector: { en: 'No working smoke detector', es: 'Detector de humo no funciona' },
    water_leaks: { en: 'Water leaks', es: 'Filtraciones de agua' },
    none: { en: 'None reported', es: 'Ninguna reportada' },
  },
  utilities: {
    yes: { en: 'Yes — utilities threatened to shut off', es: 'Sí — amenaza de corte de servicios' },
    no: { en: 'No issues', es: 'Sin problemas' },
    already_shut_off: { en: 'Already shut off', es: 'Ya cortados' },
    declined: { en: 'Declined', es: 'Prefirió no responder' },
  },
  foodFreq: {
    often_true: { en: 'Often true', es: 'Frecuentemente cierto' },
    sometimes_true: { en: 'Sometimes true', es: 'A veces cierto' },
    never_true: { en: 'Never true', es: 'Nunca cierto' },
  },
  foodLabels: {
    worried_running_out: { en: 'Worried about running out of food', es: 'Preocupación por quedarse sin comida' },
    food_didnt_last: { en: 'Food didn\'t last & no money for more', es: 'La comida no alcanzó y sin dinero para más' },
  },
  transp: {
    yes: { en: 'Lacks transportation', es: 'Falta de transporte' },
    no: { en: 'Has transportation', es: 'Tiene transporte' },
  },
  career: {
    help_finding: { en: 'Wants help finding work', es: 'Quiere ayuda para encontrar trabajo' },
    help_keeping: { en: 'Wants help keeping work', es: 'Quiere ayuda para mantener el trabajo' },
    no_help: { en: 'No employment help needed', es: 'No necesita ayuda con empleo' },
  },
  school: {
    yes: { en: 'Wants education help', es: 'Quiere ayuda con educación' },
    no: { en: 'No education help needed', es: 'No necesita ayuda con educación' },
  },
  safetyLevel: {
    never_1: { en: 'Never (1)', es: 'Nunca (1)' },
    rarely_2: { en: 'Rarely (2)', es: 'Raramente (2)' },
    sometimes_3: { en: 'Sometimes (3)', es: 'A veces (3)' },
    fairly_often_4: { en: 'Fairly often (4)', es: 'Bastante seguido (4)' },
    frequently_5: { en: 'Frequently (5)', es: 'Frecuentemente (5)' },
    declined: { en: 'Declined', es: 'Prefirió no responder' },
    not_asked: { en: 'Not asked', es: 'No preguntado' },
  },
  safetyLabels: {
    physical_hurt: { en: 'Physically hurt', es: 'Daño físico' },
    insult: { en: 'Insulted', es: 'Insultos' },
    threaten: { en: 'Threatened', es: 'Amenazas' },
    scream_curse: { en: 'Screamed/cursed at', es: 'Gritos / insultos verbales' },
  },
  physical: {
    yes: { en: 'Yes — has difficulty', es: 'Sí — tiene dificultad' },
    no: { en: 'No difficulty', es: 'Sin dificultad' },
    declined: { en: 'Declined', es: 'Prefirió no responder' },
    not_asked: { en: 'Not asked', es: 'No preguntado' },
  },
  physicalLabels: {
    walking_stairs: { en: 'Walking / climbing stairs', es: 'Caminar / subir escaleras' },
    dressing_bathing: { en: 'Dressing / bathing', es: 'Vestirse / bañarse' },
  },
  riskLabel: {
    en: 'Risk score',
    es: 'Puntaje de riesgo',
  },
  riskLow: { en: 'low risk', es: 'riesgo bajo' },
  riskMedium: { en: 'medium risk', es: 'riesgo medio' },
  riskHigh: { en: 'high risk', es: 'riesgo alto' },
  unknown: { en: 'Unknown', es: 'Desconocido' },
} as const;

function pick<T extends { en: string; es: string }>(obj: T, lang: Lang): string {
  return obj[lang] ?? obj.en;
}

// --- Per-domain humanizers --------------------------------------------------

function humanizeLivingSituation(answers: any, lang: Lang): HumanizedRow[] {
  const a = (answers?.answer ?? 'unknown') as keyof typeof t.living | 'unknown';
  const positive = a === 'unstable' || a === 'worried';
  const value = a in t.living ? pick(t.living[a as keyof typeof t.living], lang) : pick(t.unknown, lang);
  return [{ label: pick(t.domain.living_situation, lang), value, positive }];
}

function humanizeHousingConditions(answers: any, lang: Lang): HumanizedRow[] {
  const sels: string[] = Array.isArray(answers?.selections) ? answers.selections : [];
  if (sels.length === 0) {
    return [
      {
        label: pick(t.domain.housing_conditions, lang),
        value: pick(t.unknown, lang),
      },
    ];
  }
  const noneOnly = sels.length === 1 && sels[0] === 'none';
  const labelled = sels.map((s) =>
    s in t.housingCondition
      ? pick(t.housingCondition[s as keyof typeof t.housingCondition], lang)
      : s,
  );
  return [
    {
      label: pick(t.domain.housing_conditions, lang),
      value: labelled.join(', '),
      positive: !noneOnly,
    },
  ];
}

function humanizeUtilities(answers: any, lang: Lang): HumanizedRow[] {
  const a = (answers?.answer ?? 'unknown') as keyof typeof t.utilities | 'unknown';
  const positive = a === 'yes' || a === 'already_shut_off';
  const value = a in t.utilities ? pick(t.utilities[a as keyof typeof t.utilities], lang) : pick(t.unknown, lang);
  return [{ label: pick(t.domain.utilities, lang), value, positive }];
}

function humanizeFood(answers: any, lang: Lang): HumanizedRow[] {
  const rows: HumanizedRow[] = [];
  const f = answers ?? {};
  for (const k of ['worried_running_out', 'food_didnt_last'] as const) {
    const v = f[k] as keyof typeof t.foodFreq | undefined;
    const positive = v === 'often_true' || v === 'sometimes_true';
    const value = v && v in t.foodFreq ? pick(t.foodFreq[v], lang) : pick(t.unknown, lang);
    rows.push({
      label: pick(t.foodLabels[k], lang),
      value,
      positive,
    });
  }
  return rows;
}

function humanizeTransportation(answers: any, lang: Lang): HumanizedRow[] {
  const a = (answers?.answer ?? 'unknown') as keyof typeof t.transp | 'unknown';
  const positive = a === 'yes';
  const value = a in t.transp ? pick(t.transp[a as keyof typeof t.transp], lang) : pick(t.unknown, lang);
  return [{ label: pick(t.domain.transportation, lang), value, positive }];
}

function humanizeCareer(answers: any, lang: Lang): HumanizedRow[] {
  const a = (answers?.answer ?? 'unknown') as keyof typeof t.career | 'unknown';
  const positive = a === 'help_finding' || a === 'help_keeping';
  const value = a in t.career ? pick(t.career[a as keyof typeof t.career], lang) : pick(t.unknown, lang);
  return [{ label: pick(t.domain.career, lang), value, positive }];
}

function humanizeSchool(answers: any, lang: Lang): HumanizedRow[] {
  const a = (answers?.answer ?? 'unknown') as keyof typeof t.school | 'unknown';
  const positive = a === 'yes';
  const value = a in t.school ? pick(t.school[a as keyof typeof t.school], lang) : pick(t.unknown, lang);
  return [{ label: pick(t.domain.school, lang), value, positive }];
}

function humanizeSafety(answers: any, lang: Lang): HumanizedRow[] {
  const rows: HumanizedRow[] = [];
  for (const k of ['physical_hurt', 'insult', 'threaten', 'scream_curse'] as const) {
    const v = (answers?.[k] ?? 'unknown') as keyof typeof t.safetyLevel | 'unknown';
    const positive = v !== 'never_1' && v !== 'declined' && v !== 'not_asked' && v !== 'unknown';
    const value = v in t.safetyLevel ? pick(t.safetyLevel[v as keyof typeof t.safetyLevel], lang) : pick(t.unknown, lang);
    rows.push({
      label: pick(t.safetyLabels[k], lang),
      value,
      positive,
    });
  }
  // Total + risk band
  const total = typeof answers?.total_score === 'number' ? answers.total_score : null;
  if (total !== null) {
    let band = pick(t.riskLow, lang);
    let positive = false;
    if (total >= 11) {
      band = pick(t.riskHigh, lang);
      positive = true;
    } else if (total >= 7) {
      band = pick(t.riskMedium, lang);
      positive = true;
    }
    rows.push({
      label: pick(t.riskLabel, lang),
      value: `${total} / 16 (${band})`,
      positive,
    });
  }
  return rows;
}

function humanizePhysicalAbility(answers: any, lang: Lang): HumanizedRow[] {
  const rows: HumanizedRow[] = [];
  for (const k of ['walking_stairs', 'dressing_bathing'] as const) {
    const v = (answers?.[k] ?? 'unknown') as keyof typeof t.physical | 'unknown';
    const positive = v === 'yes';
    const value = v in t.physical ? pick(t.physical[v as keyof typeof t.physical], lang) : pick(t.unknown, lang);
    rows.push({
      label: pick(t.physicalLabels[k], lang),
      value,
      positive,
    });
  }
  return rows;
}

// --- Public entry point -----------------------------------------------------

export function humanizeAnswer(domain: HrsnDomain, answers: any, lang: Lang): HumanizedRow[] {
  switch (domain) {
    case 'living_situation':
      return humanizeLivingSituation(answers, lang);
    case 'housing_conditions':
      return humanizeHousingConditions(answers, lang);
    case 'utilities':
      return humanizeUtilities(answers, lang);
    case 'food':
      return humanizeFood(answers, lang);
    case 'transportation':
      return humanizeTransportation(answers, lang);
    case 'career':
      return humanizeCareer(answers, lang);
    case 'school':
      return humanizeSchool(answers, lang);
    case 'safety':
      return humanizeSafety(answers, lang);
    case 'physical_ability':
      return humanizePhysicalAbility(answers, lang);
    default:
      return [{ label: domain, value: pick(t.unknown, lang) }];
  }
}

export function humanizeDomainName(domain: HrsnDomain, lang: Lang): string {
  if (domain in t.domain) {
    return pick(t.domain[domain], lang);
  }
  return domain;
}
